import { PropertyType } from '~/client/graph/types.generated'
import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  getResourceDefinition,
  isResourcePropertyBackedField,
} from '../definitions'
import { IResourceFieldDefinition } from '../definitions/types'
import {
  DEFAULT_RESOURCE_LINK_TARGET,
  IResourceLinkConfig,
} from '../resourceLink'

interface IPropertyConfigurationOption {
  value: string
  label: string
  deleted?: boolean | null
  parentValue?: string | null
}

interface IResourcePropertySource {
  name: string
  displayName?: string | null
  propertyType: PropertyType | string
  position?: number | null
  configuration?: {
    multiline?: boolean | null
    format?: string | null
    unit?: string | null
    options?: IPropertyConfigurationOption[] | null
    resourceLink?: IResourceLinkConfig | null
  } | null
}

interface IResourceFieldLabelSource {
  name: string
  displayName?: string | null
}

export interface IResourceListProperty extends IResourceProperty {
  position: number
  isDefinitionOnly?: boolean
}

const humanize = (value: string): string =>
  value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const mapFieldDataTypeToPropertyType = (
  fieldDefinition: IResourceFieldDefinition,
): PropertyType => {
  switch (fieldDefinition.dataType) {
    case 'number':
      return PropertyType.Number
    case 'datetime':
      return PropertyType.Datetime
    case 'checkbox':
      return PropertyType.Checkbox
    case 'select':
      return PropertyType.Select
    case 'location':
      return PropertyType.ResourceLink
    default:
      return PropertyType.Text
  }
}

const hasListBehavior = (fieldDefinition: IResourceFieldDefinition): boolean =>
  !!(
    fieldDefinition.table?.sortable ||
    fieldDefinition.table?.groupable ||
    fieldDefinition.table?.filterable ||
    // A display-only column has none of the above but still needs a column.
    fieldDefinition.table?.column
  )

export const getResourceFieldLabel = (
  resourceTypeId: string,
  key: string,
  fieldDefinition?: IResourceFieldDefinition,
  properties?: IResourceFieldLabelSource[],
): string => {
  const dynamicProperty = properties?.find(property => property.name === key)
  if (dynamicProperty?.displayName) {
    return dynamicProperty.displayName
  }

  const definition = getResourceDefinition(resourceTypeId)
  const resolvedFieldDefinition =
    fieldDefinition ||
    definition?.fields[key] ||
    definition?.map?.syntheticFields?.[key]
  const translatorKey = resolvedFieldDefinition?.table?.labelKey || key
  const translated =
    Localization.translator[
      translatorKey as keyof typeof Localization.translator
    ]

  if (typeof translated === 'string') {
    return translated
  }

  return humanize(key)
}

export const buildResourceListProperties = (
  resourceTypeId: string | undefined,
  resourceProperties?: IResourcePropertySource[] | null,
): IResourceListProperty[] => {
  const properties = (resourceProperties || []).map(property => ({
    name: property.name,
    displayName:
      property.displayName ||
      getResourceFieldLabel(resourceTypeId || '', property.name),
    propertyType: property.propertyType as PropertyType,
    configuration: property.configuration
      ? {
          multiline: property.configuration.multiline || undefined,
          options: property.configuration.options?.map(option => ({
            value: option.value,
            label: option.label,
            parentValue: option.parentValue ?? undefined,
          })),
          format: property.configuration.format || undefined,
          unit: property.configuration.unit || undefined,
          resourceLink: property.configuration.resourceLink || undefined,
        }
      : undefined,
    position: property.position ?? 0,
  }))

  if (!resourceTypeId) {
    return properties
  }

  const definition = getResourceDefinition(resourceTypeId)
  if (!definition) {
    return properties
  }

  const existingKeys = new Set(properties.map(property => property.name))
  const definitionOnlyProperties = Object.entries(definition.fields)
    .filter(
      ([key, fieldDefinition]) =>
        !existingKeys.has(key) &&
        !isResourcePropertyBackedField(resourceTypeId, key) &&
        hasListBehavior(fieldDefinition),
    )
    .map(([key, fieldDefinition], index) => ({
      name: key,
      displayName: getResourceFieldLabel(resourceTypeId, key, fieldDefinition),
      propertyType: mapFieldDataTypeToPropertyType(fieldDefinition),
      // A `location` field carries the resource-link target so the display/cell
      // layer resolves the referenced location's name (parent → parent name).
      configuration:
        fieldDefinition.dataType === 'location'
          ? {
              resourceLink: {
                targetResourceType: DEFAULT_RESOURCE_LINK_TARGET,
              },
            }
          : undefined,
      position: properties.length + index,
      isDefinitionOnly: true,
    }))

  return [...properties, ...definitionOnlyProperties]
}
